import { useSplitView } from '@/app/_common/split-view/split-view-context'
import { ErrorTabContent } from '@/app/_common/split-view/tabs/content/_templates'
import { SessionTabTemplate } from '@/app/_common/split-view/tabs/content/turn-step-tabs'
import { createTurnStepTab } from '@/app/_common/split-view/tabs/factories'
import { Tab, TabTypeTurnStep } from '@/app/_common/split-view/tabs/types'
import { useTurnStepParsed } from '@/app/chat/_common/use-turn-parsed'
import { Button } from '@/components/ui/button'
import { LoadingDots } from '@/components/ui/loading-dots'
import { usePauseJourneyMutation } from '@/hooks/api/use-pause-journey-mutation'
import { useResumeJourneyMutation } from '@/hooks/api/use-resume-journey-mutation'
import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'
import { CheckCircle2, TriangleAlertIcon } from 'lucide-react'
import React from 'react'

export function InteractiveLiveBrowserSession({
  workflowId,
  turnId,
  stepId,
}: Readonly<{ workflowId: string; turnId: string; stepId: string }>) {
  const parsedStep = useTurnStepParsed({
    turnId,
    stepId,
    workflowId,
  })
  const { addTab, getTabPositionMetadata, filterTabs } = useSplitView()
  const { journeyViewReference, liveSessionView, isPaused, isActionRequired } =
    parsedStep?.outputDisplayData ?? {}
  const [pendingAction, setPendingAction] = React.useState<'pause' | 'resume' | null>(null)

  const pauseMutation = usePauseJourneyMutation(
    { workflowId, turnId },
    {
      onMutate: () => setPendingAction('pause'),
    },
  )
  const resumeMutation = useResumeJourneyMutation(
    { workflowId, turnId },
    {
      onMutate: () => setPendingAction('resume'),
    },
  )

  // Track when server data updates to clear pending state
  React.useEffect(() => {
    if ((pendingAction === 'pause' && isPaused) || (pendingAction === 'resume' && !isPaused)) {
      setPendingAction(null)
    }
  }, [isPaused, pendingAction])

  const recordingUrl = parsedStep?.outputDisplayData.recordingUrl

  // if the recording url becomes available, we need to change the tab type to
  // prevent the expired live session tab from being displayed
  React.useEffect(() => {
    if (recordingUrl) {
      const predicate = (t: Tab) =>
        t.type === TabTypeTurnStep.LiveSession &&
        t.turnId === turnId &&
        t.stepId === stepId &&
        t.workflowId === workflowId

      const position = getTabPositionMetadata({ predicate })

      addTab({
        ...position,
        tab: createTurnStepTab({
          type: TabTypeTurnStep.RecordedSession,
          turnId,
          stepId,
          workflowId,
        }),
      })
      filterTabs({ predicate })
    }
  }, [addTab, getTabPositionMetadata, turnId, stepId, workflowId, recordingUrl, filterTabs])

  if (!parsedStep) {
    return null
  }

  if (!journeyViewReference || !liveSessionView) {
    return (
      <div className='aspect-[1279/719] w-full'>
        <ErrorTabContent />
      </div>
    )
  }

  return (
    <SessionTabTemplate
      status={parsedStep?.status}
      description={parsedStep?.outputDisplayData.description}
      footer={
        parsedStep?.outputDisplayData.isActionRequired && (
          <div className='flex items-center gap-4 rounded-lg border border-neutral-75 bg-neutral-25 p-4 text-sm'>
            <TriangleAlertIcon className='size-6 shrink-0 text-error-300' />
            {parsedStep?.outputDisplayData.actionRequiredMessage}
            <Button size='xs' onClick={() => resumeMutation.mutate(journeyViewReference)}>
              {pendingAction === 'resume' && <LoadingDots className='absolute' />}
              <span
                className={cn('flex items-center gap-2', pendingAction === 'resume' && 'opacity-0')}
              >
                <CheckCircle2 className='size-4 shrink-0' />
                Return control to Agent
              </span>
            </Button>
          </div>
        )
      }
    >
      <div className='group/lvs relative flex aspect-[1279/719] w-full cursor-auto select-none justify-center overflow-hidden rounded-lg'>
        <div
          className={overlayVariants({
            visible: !isActionRequired && (!isPaused || Boolean(pendingAction)),
            interactive: !isActionRequired && !isPaused && !pendingAction,
          })}
        />
        <iframe
          title='Live session view'
          src={liveSessionView}
          className='aspect-[1279/719] w-full'
          aria-label='Interactive browser session'
        />
        <div
          role='toolbar'
          aria-label='Session control'
          className={toolbarVariants({
            visible: !isActionRequired && (isPaused || Boolean(pendingAction)),
            interactive: !isActionRequired && !isPaused,
            shadow: isPaused,
          })}
        >
          {isPaused ? (
            <div className='flex items-center gap-2'>
              <span className='text-xs' aria-live='polite'>
                You are in control of the browser.
              </span>
              <Button
                onClick={() => resumeMutation.mutate(journeyViewReference)}
                size='xs'
                className='normal-case tracking-wide disabled:bg-neutral-600 disabled:opacity-100'
                disabled={pendingAction === 'resume'}
                aria-label='Return control to Agent'
              >
                {pendingAction === 'resume' && <LoadingDots className='absolute' />}
                <span className={cn(pendingAction === 'resume' && 'opacity-0')}>
                  Return control to Agent
                </span>
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => pauseMutation.mutate(journeyViewReference)}
              size='xs'
              className='normal-case tracking-wide disabled:bg-neutral-600 disabled:opacity-100'
              disabled={pendingAction === 'pause'}
              aria-label='Take control of the browser session'
            >
              {pendingAction === 'pause' && <LoadingDots className='absolute' />}
              <span className={cn(pendingAction === 'pause' && 'opacity-0')}>Take Control</span>
            </Button>
          )}
        </div>
      </div>
    </SessionTabTemplate>
  )
}

const overlayVariants = cva(
  'absolute left-0 top-0 h-full w-full rounded-lg outline-1 -outline-offset-1 outline-neutral-500 transition-all ease-in-out',
  {
    variants: {
      visible: {
        true: 'bg-black/60 outline',
        false: '',
      },
      interactive: {
        true: 'opacity-0 group-focus-within/lvs:opacity-100 group-focus-within/lvs:bg-black/60 group-focus-within/lvs:outline group-hover/lvs:opacity-100 group-hover/lvs:bg-black/60 group-hover/lvs:outline group-focus/lvs:opacity-100 group-focus/lvs:bg-black/60 group-focus/lvs:outline',
        false: '',
      },
    },
    defaultVariants: {
      visible: false,
      interactive: false,
    },
    compoundVariants: [
      {
        visible: false,
        interactive: false,
        class: 'hidden',
      },
    ],
  },
)

const toolbarVariants = cva(
  'absolute -bottom-[42px] flex h-[42px] w-fit items-center rounded-t-2xl border border-b-0 border-white bg-neutral-25 p-2 transition-all duration-75 ease-in-out',
  {
    variants: {
      visible: {
        true: '-translate-y-[42px]',
        false: '',
      },
      interactive: {
        true: 'group-focus-within/lvs:-translate-y-[42px] group-hover/lvs:-translate-y-[42px] group-focus/lvs:-translate-y-[42px]',
        false: '',
      },
      shadow: {
        true: 'shadow-[0_0_12px_0_rgba(0,0,0,0.35)]',
        false: '',
      },
    },
    defaultVariants: {
      visible: false,
      interactive: false,
      shadow: false,
    },
    compoundVariants: [
      {
        visible: false,
        interactive: false,
        class: 'hidden',
      },
    ],
  },
)
